import React from 'react';
import {
  Box,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { DeleteOutlined } from '@mui/icons-material';
import { NumericFormat } from 'react-number-format';

import AddButtonAutocomplete from '@/__core/hocs/AddButtonAutocomplete';
import RequestMaterialItemCreateModal from '@/modules/inventory/requestMaterial/components/RequestMaterialItemCreateModal';
import { sharedFormStyles } from '@/__core/styles/sharedFormStyles';
import { calculateTableWidth } from '@/utils/tableWidth';
import { useSidebarState } from '@/__core/hooks/useSidebarState';

import { InventoryIssueFormLine } from '../types';

const tableStyles = sharedFormStyles.orderItemsTableStyles;
const PAPER_HORIZONTAL_PADDING = 0;

interface StockBalanceSnapshot {
  on_hand_qty: number;
  allocated_qty: number;
  available_qty: number;
  last_price?: number;
  uom_name?: string;
}

const editableCellSx = {
  minWidth: 0,
  '& .MuiInputBase-root': {
    height: '30px !important',
    minHeight: '30px !important',
    maxHeight: '30px !important',
    backgroundColor: 'transparent',
    border: 'none !important',
    borderBottom: '1px solid #d1d5db',
    borderRadius: '0 !important',
    '&:hover': {
      borderBottom: '1px solid #9ca3af',
    },
    '&.Mui-focused': {
      borderBottom: '1px solid #3b82f6',
    },
  },
  '& .MuiOutlinedInput-notchedOutline': {
    border: 'none !important',
  },
  '& .MuiInputBase-input': {
    padding: '6px 0 !important',
    fontSize: '0.875rem',
    color: '#374151 !important',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
};


interface IssueLinesTableProps {
  lines: InventoryIssueFormLine[];
  itemOptions: Array<{ id: string; name: string; code: string; unit_id?: string; unit_name?: string }>;
  uomOptionsByLineId?: Record<number, Array<{ id: string; name: string }>>;
  itemLoading?: boolean;
  stockByLineId?: Record<number, StockBalanceSnapshot | null>;
  stockLoadingByLineId?: Record<number, boolean>;
  stockErrorByLineId?: Record<number, boolean>;
  companyId?: string;
  companyName?: string;
  onChangeLine: (lineId: number, updates: Partial<InventoryIssueFormLine>) => void;
  onAddLine: () => void;
  onRemoveLine: (lineId: number) => void;
  onCreateItem?: (item: { id: string; name: string; code: string; unit_id?: string; unit_name?: string }) => void;
  canCreateItem?: boolean;
  searchItems?: (query: string) => Promise<Array<{ id: string; name: string; code: string; unit_id?: string; unit_name?: string }>>;
  searchLoading?: boolean;
  disabled?: boolean;
}

export default function IssueLinesTable({
  lines,
  itemOptions,
  uomOptionsByLineId = {},
  itemLoading = false,
  stockByLineId = {},
  stockLoadingByLineId = {},
  stockErrorByLineId = {},
  onChangeLine,
  onAddLine,
  onRemoveLine,
  disabled = false,
  companyId = '',
  companyName,
  onCreateItem,
  canCreateItem = false,
  searchItems,
  searchLoading = false,
}: IssueLinesTableProps) {
  const safeLines = lines || [];
  const [manualItemOptions] = React.useState<Array<{ id: string; name: string; code: string }>>([]);
  const [searchResults, setSearchResults] = React.useState<Array<{ id: string; name: string; code: string; unit_id?: string; unit_name?: string }>>([]);
  const [isSearching, setIsSearching] = React.useState(false);
  const searchInputRef = React.useRef('');
  const [tableWidth, setTableWidth] = React.useState<number>(0);
  const sidebarOpen = useSidebarState();

  React.useEffect(() => {
    const handleResize = () => {
      setTableWidth(calculateTableWidth({
        windowWidth: window.innerWidth,
        sidebarOpen,
      }));
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [sidebarOpen]);

  const ItemCreateModal = React.useMemo(
    () => (props: { open: boolean; onClose: () => void; onSave: (data: any) => Promise<any>; loading?: boolean }) => (
      <RequestMaterialItemCreateModal {...props} companyId={companyId} companyName={companyName} />
    ),
    [companyId, companyName],
  );

  const effectiveItemOptions = React.useMemo(() => {
    const merged = isSearching ? searchResults : [...itemOptions, ...manualItemOptions, ...searchResults];
    const unique = new Map<string, { id: string; name: string; code: string }>();
    merged.forEach((item) => {
      if (!unique.has(item.id)) {
        unique.set(item.id, item);
      }
    });
    return Array.from(unique.values());
  }, [itemOptions, manualItemOptions, searchResults, isSearching]);

  const formatQty = (value: number | string | null | undefined) => {
    const numericValue = Number(value);
    if (Number.isNaN(numericValue)) {
      return '0';
    }
    return numericValue.toLocaleString('vi-VN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const formatMoney = (value: number | string | null | undefined) => {
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue)) {
      return '0';
    }
    return numericValue.toLocaleString('vi-VN', { maximumFractionDigits: 2 });
  };

  const resolveUnitCost = (line: InventoryIssueFormLine) => {
    const enteredUnitCost = Number(line.unit_cost) || 0;
    if (enteredUnitCost > 0) {
      return enteredUnitCost;
    }
    return Number(stockByLineId[line.id]?.last_price) || 0;
  };

  const totalIssuedQty = React.useMemo(
    () => safeLines.reduce((sum, line) => sum + (Number(line.issued_qty) || 0), 0),
    [safeLines],
  );

  const totalLineAmount = React.useMemo(
    () => safeLines.reduce((sum, line) => sum + ((Number(line.issued_qty) || 0) * resolveUnitCost(line)), 0),
    [safeLines],
  );

  return (
    <Box sx={tableStyles.sectionWrapper}>
      <TableContainer sx={tableStyles.tableContainer(tableWidth, PAPER_HORIZONTAL_PADDING)}>
        <Table size='small' sx={{ ...tableStyles.table, width: '100%' }}>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f9fafb' }}>
              <TableCell style={{ width: '40px', minWidth: '40px' }} sx={{ fontWeight: 600, color: '#374151', textAlign: 'center' }}>#</TableCell>
              <TableCell style={{ width: '200px', minWidth: '200px' }} sx={{ fontWeight: 600, color: '#374151' }}>Vật tư</TableCell>
              <TableCell style={{ width: '120px', minWidth: '120px' }} sx={{ fontWeight: 600, color: '#374151', textAlign: 'center' }}>Tồn kho</TableCell>
              <TableCell style={{ width: '120px', minWidth: '120px' }} sx={{ fontWeight: 600, color: '#374151', textAlign: 'center' }}>Đã phân bổ</TableCell>
              <TableCell style={{ width: '100px', minWidth: '100px' }} sx={{ fontWeight: 600, color: '#374151' }}>ĐVT</TableCell>
              <TableCell style={{ width: '120px', minWidth: '120px' }} sx={{ fontWeight: 600, color: '#374151', textAlign: 'right' }}>SL xuất</TableCell>
              <TableCell style={{ width: '140px', minWidth: '140px' }} sx={{ fontWeight: 600, color: '#374151', textAlign: 'right' }}>Đơn giá</TableCell>
              <TableCell style={{ width: '150px', minWidth: '150px' }} sx={{ fontWeight: 600, color: '#374151', textAlign: 'right' }}>Thành tiền</TableCell>
              <TableCell style={{ width: '180px', minWidth: '180px' }} sx={{ fontWeight: 600, color: '#374151' }}>Ghi chú</TableCell>
              <TableCell style={{ width: '48px', minWidth: '48px' }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {safeLines.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} sx={{ textAlign: 'center', py: 4, color: '#9ca3af', fontSize: '0.875rem' }}>
                  Chưa có dòng vật tư. Nhấn &quot;Thêm item&quot; để bắt đầu.
                </TableCell>
              </TableRow>
            ) : (
              safeLines.map((line, index) => {
                const selectedItem = effectiveItemOptions.find((item) => item.id === line.item_id) || null;
                const lineUomOptions = uomOptionsByLineId[line.id] || [];
                const selectedUom = lineUomOptions.find((option) => option.id === line.uom_id) || null;
                return (
                  <TableRow key={line.id} sx={{ '&:hover': { backgroundColor: '#fafafa' } }}>
                    <TableCell sx={{ textAlign: 'center', color: '#6b7280', fontSize: '0.75rem', fontWeight: 500 }}>{index + 1}</TableCell>
                    <TableCell>
                      <AddButtonAutocomplete
                        options={effectiveItemOptions}
                        value={selectedItem || (line.item_id ? { id: line.item_id, name: line.item_name, code: line.item_code } : null)}
                        getOptionLabel={(option) => option?.code ? `${option.code} - ${option.name}` : option?.name || ''}
                        renderOption={(props, option) => (
                          <Box component='li' {...props} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.75 }}>
                            <Typography sx={{ fontSize: '0.72rem', color: '#6b7280', fontFamily: '"Roboto Mono", monospace' }}>{option.code}</Typography>
                            <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: '#111827' }}>{option.name}</Typography>
                          </Box>
                        )}
                        onChange={(_event, newValue) => {
                          const nextItem = newValue as { id: string; name: string; code: string; unit_id?: string; unit_name?: string; last_price?: string } | null;
                          const lastPrice = Number(nextItem?.last_price) || 0;
                          onChangeLine(line.id, {
                            item_id: nextItem?.id || '',
                            item_code: nextItem?.code || '',
                            item_name: nextItem?.name || '',
                            uom_id: nextItem?.unit_id || '',
                            uom_name: nextItem?.unit_name || '',
                            unit_cost: lastPrice,
                            line_amount: (Number(line.issued_qty) || 0) * lastPrice,
                          });
                        }}
                        onAddNewCustom={async (createdItem: { id: string; name: string; code: string; unit_id?: string; unit_name?: string }) => {
                          onChangeLine(line.id, {
                            item_id: createdItem.id,
                            item_code: createdItem.code,
                            item_name: createdItem.name,
                            uom_id: createdItem.unit_id || '',
                            uom_name: createdItem.unit_name || '',
                            unit_cost: 0,
                            line_amount: 0,
                          });
                          onCreateItem?.(createdItem);
                          return createdItem;
                        }}
                        CustomDialog={ItemCreateModal}
                        placeholder='Chọn vật tư'
                        noOptionsText='Không tìm thấy vật tư'
                        onInputChange={(_event, value, reason) => {
                          if (reason === 'input' && searchItems && value.trim()) {
                            searchInputRef.current = value;
                            setIsSearching(true);
                            searchItems(value.trim()).then((results) => {
                              if (searchInputRef.current === value) {
                                setSearchResults(results);
                              }
                            });
                          } else if (reason === 'clear' || !value.trim()) {
                            searchInputRef.current = '';
                            setIsSearching(false);
                            setSearchResults([]);
                          }
                        }}
                        loading={itemLoading || searchLoading}
                        disableClearable={Boolean(line.item_id)}
                        disabled={disabled}
                        canAddNew={!disabled && canCreateItem}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            size='small'
                            placeholder='Chọn vật tư'
                            sx={{
                              '& .MuiAutocomplete-clearIndicator': { display: 'none' },
                              '& .MuiAutocomplete-popupIndicator': { display: 'none' },
                            }}
                          />
                        )}
                        fullWidth
                        sx={editableCellSx}
                      />
                    </TableCell>
                    <TableCell sx={{ textAlign: 'center' }}>
                      <Typography sx={{ fontSize: '0.875rem', fontWeight: 700, color: '#374151' }}>
                        {formatQty(line.item_id ? Number(stockByLineId[line.id]?.on_hand_qty) || 0 : 0)}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ textAlign: 'center' }}>
                      <Typography sx={{ fontSize: '0.875rem', fontWeight: 700, color: '#374151' }}>
                        {formatQty(line.item_id ? Number(stockByLineId[line.id]?.allocated_qty) || 0 : 0)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <AddButtonAutocomplete
                        options={lineUomOptions}
                        value={selectedUom || (line.uom_id ? { id: line.uom_id, name: line.uom_name } : null)}
                        onChange={(_event, newValue) => {
                          const nextUom = newValue as { id: string; name: string } | null;
                          onChangeLine(line.id, {
                            uom_id: nextUom?.id || '',
                            uom_name: nextUom?.name || '',
                          });
                        }}
                        placeholder='Chọn ĐVT'
                        noOptionsText='Không tìm thấy ĐVT'
                        disabled={disabled}
                        canAddNew={false}
                        fullWidth
                        sx={editableCellSx}
                      />
                    </TableCell>
                    <TableCell>
                      <NumericFormat
                        customInput={TextField}
                        size='small'
                        value={line.issued_qty}
                        onValueChange={({ floatValue }) => {
                          const issuedQty = floatValue || 0;
                          onChangeLine(line.id, {
                            issued_qty: issuedQty,
                            line_amount: issuedQty * resolveUnitCost(line),
                          });
                        }}
                        decimalScale={2}
                        fixedDecimalScale
                        thousandSeparator='.'
                        decimalSeparator=','
                        allowNegative={false}
                        inputProps={{ style: { textAlign: 'right' } }}
                        disabled={disabled}
                        fullWidth
                        sx={editableCellSx}
                      />
                    </TableCell>
                    <TableCell>
                      <NumericFormat
                        customInput={TextField}
                        size='small'
                        value={line.unit_cost}
                        onValueChange={({ floatValue }) => {
                          const cost = floatValue || 0;
                          onChangeLine(line.id, {
                            unit_cost: cost,
                            line_amount: (Number(line.issued_qty) || 0) * cost,
                          });
                        }}
                        decimalScale={0}
                        thousandSeparator='.'
                        decimalSeparator=','
                        allowNegative={false}
                        inputProps={{ style: { textAlign: 'right' } }}
                        disabled={disabled}
                        fullWidth
                        sx={editableCellSx}
                      />
                    </TableCell>
                    <TableCell sx={{ textAlign: 'right' }}>
                      <Typography sx={{ fontSize: '0.875rem', fontWeight: 700, color: '#374151' }}>
                        {formatMoney((Number(line.issued_qty) || 0) * resolveUnitCost(line))}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <TextField
                        size='small'
                        value={line.note || ''}
                        onChange={(event) => onChangeLine(line.id, { note: event.target.value })}
                        disabled={disabled}
                        fullWidth
                        sx={editableCellSx}
                      />
                    </TableCell>
                    <TableCell sx={{ textAlign: 'center' }}>
                      <IconButton
                        size='small'
                        onClick={() => onRemoveLine(line.id)}
                        disabled={disabled}
                        sx={{ color: '#ef4444' }}
                      >
                        <DeleteOutlined fontSize='small' />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
          {safeLines.length > 0 && (
            <tfoot>
              <TableRow sx={{ backgroundColor: '#f9fafb' }}>
                <TableCell colSpan={5} sx={{ py: 0.5, borderTop: '2px solid #e5e7eb' }} />
                <TableCell sx={{ textAlign: 'right', fontWeight: 700, fontSize: '0.875rem', color: '#111827', py: 0.5, borderTop: '2px solid #e5e7eb', pr: 1 }}>
                  {totalIssuedQty.toLocaleString('vi-VN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </TableCell>
                <TableCell sx={{ py: 0.5, borderTop: '2px solid #e5e7eb' }} />
                <TableCell sx={{ textAlign: 'right', fontWeight: 700, fontSize: '0.875rem', color: '#111827', py: 0.5, borderTop: '2px solid #e5e7eb', pr: 1 }}>
                  {totalLineAmount.toLocaleString('vi-VN', { maximumFractionDigits: 0 })}
                </TableCell>
                <TableCell sx={{ py: 0.5, borderTop: '2px solid #e5e7eb' }} />
                <TableCell sx={{ py: 0.5, borderTop: '2px solid #e5e7eb' }} />
              </TableRow>
            </tfoot>
          )}
        </Table>
      </TableContainer>

      {!disabled && (
        <Box sx={{ display: 'flex', alignItems: 'center', px: 1, py: 1, gap: 2 }}>
          <Button
            variant='contained'
            size='small'
            onClick={onAddLine}
            disabled={disabled}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            Thêm item
          </Button>
        </Box>
      )}
    </Box>
  );
}
